import React from 'react';
import { useRouter } from 'next/router';
import classNames from 'classnames';

import analytics from 'common/analytics';
import { MenuItemLink } from 'components/navigation';
import Carousel from 'components/ui/Carousel';
import { APP_ROUTES } from 'constants/routes';
import { useCategories } from 'hooks/data/useCategories';

import type { CarouselApi } from 'components/ui/Carousel';
import type { ICategory } from 'types';

import styles from './HeaderCategoriesNavigation.module.scss';

const HeaderCategoriesNavigation = () => {

  const [carouselApi, setCarouselApi] = React.useState<CarouselApi>();

  // Hooks
  const { data: categories } = useCategories();

  const router = useRouter();

  // Props

  const items = React.useMemo(() => {
    if (!categories?.length) return [];
    return categories.map((category) => parseItem(
      category,
      APP_ROUTES.CATEGORIES,
      APP_ROUTES.CATEGORIES_FILTERED,
    ));
  }, [categories]);

  const { asPath: currentPath } = router;

  // Effects

  React.useEffect(() => {
    if (isCategoriesPathname(currentPath)) {
      if (carouselApi) {
        const activeIndex = items.findIndex(({ href, matchHref }) => (
          currentPath.startsWith(href) || currentPath.startsWith(matchHref)
        ));
        const slidesInView = carouselApi.slidesInView();
        if (!slidesInView.includes(activeIndex) || (slidesInView[0] === activeIndex || slidesInView[slidesInView.length - 1] === activeIndex)) {
          carouselApi.scrollTo(activeIndex, !slidesInView?.length);
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath, carouselApi]);

  const onInit = (api: CarouselApi) => {
    setCarouselApi(api);
  };

  // Empty

  if (!items?.length) return null;

  // Render

  return (
    <Carousel
      prevClassName={classNames(styles.navButton, styles.prev)}
      nextClassName={classNames(styles.navButton, styles.next)}
      prevIconClassName={styles.icon}
      nextIconClassName={styles.icon}
      className={styles.root}
      spaceBetween={8}
      navigation
      dragFree
      onInit={onInit}
    >
      {
        items.map((item, index) => {
          return (
            <div className={styles.menuItem} key={`slide-${item.href}-${index}`}>
              <MenuItemLink
                className={styles.menuItemLink}
                activeClassName={styles.active}
                iconClassName={styles.icon}
                item={item}
                onClick={() => {
                  analytics.selectCategory(item?.label);
                }}
              />
            </div>
          );
        })
      }
    </Carousel>
  );
};

// Helpers

const parseItem = (item: ICategory, baseHref: string, matchBaseHref: string) => {
  const {
    slug, name: label, iconPng,
  } = item;

  return {
    href: `${baseHref}/${slug}`,
    matchHref: `${matchBaseHref}/${slug}`,
    id: slug,
    slug,
    label,
    image: iconPng,
  };
};

const isCategoriesPathname = (path: string) => {
  return path && (path.startsWith(APP_ROUTES.CATEGORIES) || path.startsWith(APP_ROUTES.CATEGORIES_FILTERED));
};

export default HeaderCategoriesNavigation;
